import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import chunk from 'lodash/chunk';
import { connect } from 'react-redux';
import { FormattedMessage, defineMessages } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import { getKeys } from 'Redux/ducks/orders';
import { getReviewsByUserId } from 'Redux/ducks/user-review';
import { history } from 'Redux/store';
import attachReviewsToItems from 'Helpers/Order/OrderItemReviewHelper';
import ConfirmAccount from 'Components/Account/ConfirmAccount';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import KeyLibrarySearch from 'Helpers/Order/KeyLibrarySearch';
import Loading from 'Components/Utility/Loading';
import Meta from 'Components/Utility/SEO/Meta';
import OrderItem from 'Components/Order/OrderItem';
import Paginate from 'Components/Utility/Paginate';
import sortByName from 'Helpers/Sort/SortByName';

import 'Components/Account/ProductLibrary/ProductLibrary.scss';

const messages = defineMessages({
  game: {
    id: 'key-library.game',
    defaultMessage: 'Game',
  },
  alphabetical: {
    id: 'key-library.alphabetical',
    defaultMessage: 'Alphabetical',
  },
  search_placeholder: {
    id: 'key-library.searchplaceholder',
    defaultMessage: 'Search product or bundle',
  },
  purchase_date: {
    id: 'key-library.purchasedate',
    defaultMessage: 'Purchase Date',
  },
  all: {
    id: 'key-library.all',
    defaultMessage: 'All',
  },
  windows: {
    id: 'key-library.windows',
    defaultMessage: 'Windows',
  },
  mac: {
    id: 'key-library.mac',
    defaultMessage: 'Mac',
  },
  linux: {
    id: 'key-library.linux',
    defaultMessage: 'Linux',
  },
  dlc: {
    id: 'key-library.dlc',
    defaultMessage: 'DLC',
  },
  book: {
    id: 'key-library.book',
    defaultMessage: 'Book',
  },
  comic: {
    id: 'key-library.comic',
    defaultMessage: 'Comic',
  },
  software: {
    id: 'key-library.software',
    defaultMessage: 'Software',
  },
  elearning: {
    id: 'key-library.elearning',
    defaultMessage: 'eLearning',
  },
  title: {
    id: 'keylibrarycontainer.seotitle',
    defaultMessage: 'Product Library',
  },
});

const KeyLibrary = ({
  intl: { formatMessage },
  emailConfirmed,
}) => {
  const showUnrevealed = getUrlParameter('unrevealed') === 'true';
  const [items, setItems] = useState([]);
  const [sort, setSort] = useState('purchase');
  const [productType, setProductType] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [hideRevealed, setHideRevealed] = useState(showUnrevealed);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const callGetkeys = async () => {
      try {
        const keyItems = await getKeys();
        const reviews = await getReviewsByUserId();
        const keyItemReviews = attachReviewsToItems(keyItems, reviews);
        setItems(keyItemReviews);
      } catch (e) {
        setFetchError(true);
      }
      setHasLoaded(true);
    };
    callGetkeys();
  }, []);

  const onSortByChange = (value) => {
    setSort(value);
    history.push('/product-library');

    if (value === 'alphabetical') {
      const sortedItems = sortByName(items);
      setItems(sortedItems);
    }

    if (value === 'purchase') {
      const sortedItems = items.sort((a, b) => new Date(b.purchased) - new Date(a.purchased));
      setItems(sortedItems);
    }
  };

  const onFilterChange = (value, setFunc) => {
    setFunc(value);
    history.push('/product-library');
  };

  const updateHits = (pageToUpdate) => {
    if (pageToUpdate !== currentPage) {
      setCurrentPage(pageToUpdate);
    }
  };

  const paidOrder = items.find(item => get(item, 'order.payment.status') !== 'REDEEM_CODE');

  let filteredItems = items;
  if (hideRevealed) {
    filteredItems = filteredItems.filter(item =>
      !['revealed', 'expired'].includes(item.status) || (item.mysteryRevealed && !item.key));
  }
  if (platform !== 'all') filteredItems = filteredItems.filter(item => get(item, `platforms.${platform}`));
  if (productType !== 'all') filteredItems = filteredItems.filter(item => item.type === productType);
  if (searchQuery !== '') filteredItems = KeyLibrarySearch(filteredItems, searchQuery);

  let chunkedItems = [];
  const nbPages = Math.ceil((filteredItems.length || 0) / 20);

  if (!isEmpty(filteredItems)) {
    chunkedItems = chunk(filteredItems, 20)[(currentPage - 1)];
  }

  return (
    <div className="account-content product-library">
      <Meta brandTitle title={formatMessage(messages.title)} />
      <h1>
        <FormattedMessage id="key-library.header" defaultMessage="Product Library" />
      </h1>
      {paidOrder &&
        <ConfirmAccount />
      }
      <div className="key-library-filters-container">
        <div className="drop-downs-section">
          <div className="dropdown-title-segment">
            <div className="mr-3"><FormattedMessage id="keylibrary.sortby" defaultMessage="Sort by" />:</div>
            <div className="dropdown-container sort-by">
              <select value={sort} onChange={e => onSortByChange(e.target.value)}>
                <option value="purchase">{formatMessage(messages.purchase_date)}</option>
                <option value="alphabetical">{formatMessage(messages.alphabetical)}</option>
              </select>
            </div>
          </div>
          <div className="dropdown-title-segment">
            <div className="mr-3"><FormattedMessage id="keylibrary.producttype" defaultMessage="Product Type" />:</div>
            <div className="dropdown-container sort-by">
              <select
                value={productType}
                onChange={e => onFilterChange(e.target.value, setProductType)}
              >
                <option value="all">{formatMessage(messages.all)}</option>
                <option value="game">{formatMessage(messages.game)}</option>
                <option value="dlc">{formatMessage(messages.dlc)}</option>
                <option value="book">{formatMessage(messages.book)}</option>
                <option value="software">{formatMessage(messages.software)}</option>
                <option value="comic">{formatMessage(messages.comic)}</option>
                <option value="elearning">{formatMessage(messages.elearning)}</option>
              </select>
            </div>
          </div>
          <div className="dropdown-title-segment">
            <div className="mr-3"><FormattedMessage id="keylibrary.platform" defaultMessage="Operating System" />:</div>
            <div className="dropdown-container sort-by">
              <select value={platform} onChange={e => onFilterChange(e.target.value, setPlatform)}>
                <option value="all">{formatMessage(messages.all)}</option>
                <option value="windows">{formatMessage(messages.windows)}</option>
                <option value="mac">{formatMessage(messages.mac)}</option>
                <option value="linux">{formatMessage(messages.linux)}</option>
              </select>
            </div>
          </div>
          <div className="checkbox-container">
            <label htmlFor="hide-redeemed">
              <FormattedMessage id="key-library.revealed" defaultMessage="Hide Revealed" />
            </label>
            <input
              id="hide-redeemed"
              name="hide-redeemed"
              type="checkbox"
              checked={hideRevealed}
              aria-label="hide redeemed"
              onChange={e => onFilterChange(e.target.checked, setHideRevealed)}
            />
          </div>
        </div>
        <div className="autosearch-container">
          <div className="search-title"><FormattedMessage id="keylibrary.search" defaultMessage="search" />:</div>
          <div className="autosearch">
            <div className="input-group">
              <input
                type="search"
                aria-label="search"
                className="form-control autosearch-form"
                placeholder={formatMessage(messages.search_placeholder)}
                title={formatMessage(messages.search_placeholder)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                enterKeyHint="search"
                value={searchQuery}
                onChange={e => onFilterChange(e.target.value, setSearchQuery)}
              />
              <span className="input-group-append pr-3">
                <FontAwesomeIcon icon={faMagnifyingGlass} color="#bdbdbd" />
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="key-list-container">
        {hasLoaded && isEmpty(filteredItems) && !isEmpty(items) && !fetchError &&
          <div className="d-flex flex-column align-items-center mt-5">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <h3 className="pt-3">
              <FormattedMessage id="key-library.results.help" defaultMessage="We couldn't find any results for you." />
            </h3>
            <p>
              <FormattedMessage
                id="key-library.search-terms"
                defaultMessage="Try adjusting any applied filters or the terms of your search"
              />
            </p>
          </div>
        }
        {hasLoaded && isEmpty(items) && !fetchError &&
          <div className="text-center">
            <h3 className="pt-3">
              <FormattedMessage id="key-library.results.emptykeylibrary" defaultMessage="Your Key Library is currently empty" />
            </h3>
            <p>
              <FormattedMessage
                id="key-library.emptylibrarymessage"
                defaultMessage="The Key Library makes it possible to view all keys from past orders in one place. Product keys will appear here once you have made an order with Fanatical."
              />
            </p>
          </div>
        }
        {hasLoaded && fetchError &&
          <div className="d-flex flex-column align-items-center mt-5">
            <h3 className="pt-3">
              <FormattedMessage id="key-library.results.error" defaultMessage="Error" />
            </h3>
            <p>
              <FormattedMessage
                id="key-library.error"
                defaultMessage="There was a problem fetching your keys. This could be due to the amount of products in your Library. If the problem persists please access your keys in the Order History page."
              />
            </p>
          </div>
        }
        {!hasLoaded && <Loading />}
        {!isEmpty(chunkedItems) &&
          chunkedItems.map(item =>
            <OrderItem
              showKeyLibraryExtras
              key={item.iid}
              game={item}
              orderID={get(item, 'order._id')}
              bundleID={get(item, 'bid')}
              order={get(item, 'order')}
              emailConfirmed={emailConfirmed}
            />,
          )}
      </div>
      <div className={`${nbPages <= 1 && currentPage <= 1 ? 'd-none' : ''}`}>
        {!isEmpty(items) && <Paginate
          hits={{ nbPages, page: currentPage - 1 }}
          updateHits={updateHits}
          pageLimit={100}
        />}</div>
    </div>
  );
};


KeyLibrary.propTypes = {
  emailConfirmed: PropTypes.bool.isRequired,
};

KeyLibrary.defaultProps = {
  email: '',
};

const mapStateToProps = ({
  auth: {
    email_confirmed: emailConfirmed,
  },
}) => ({
  emailConfirmed,
});

export default connect(mapStateToProps)(KeyLibrary);
